import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import LeftMenu from "../components/LeftMenu";
import ThreadList from "../components/messages/ThreadList";
import LiveReconnectingNotice from "../components/messages/LiveReconnectingNotice";
import ThreadView from "../components/messages/ThreadView";
import { usePolling } from "../features/messages/hooks/usePolling";
import { useMessagesLive } from "../features/messages/live/useMessagesLive";
import { STAFF_CHANNEL } from "../features/messages/models/MessagesChannel";
import { selectThreads, selectThreadsStatus } from "../features/messages/selectors/messages-selectors";
import { fetchThreads } from "../features/messages/slice-extensions/messages-slice-extension";
import { LoadingStatus } from "../features/sessions/models/LoadingStatus";
import { ROUTES } from "../routes";

/**
 * The organization's conversations. Phone first: the list, or one thread; from `lg` the
 * list and the open thread sit side by side. Live while connected; refreshed every 30 s otherwise.
 */
const MessagesPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { threadId } = useParams<{ threadId?: string }>();
  const threads = useAppSelector(selectThreads(STAFF_CHANNEL));
  const status = useAppSelector(selectThreadsStatus);
  const active = threads.find((thread) => thread.id === threadId);

  const refresh = () => {
    dispatch(fetchThreads(STAFF_CHANNEL));
  };
  const live = useMessagesLive(STAFF_CHANNEL, { onUnreadChanged: refresh, onPresenceChanged: refresh, onResync: refresh });
  usePolling(refresh, live !== "connected");

  const threadLink = (id: string) => ROUTES.MESSAGE_THREAD.replace(":threadId", id);

  return (
    <section>
      <div className="flex h-full gap-4">
        <LeftMenu />
        <div className="w-full min-w-0 p-4 pb-10 lg:px-10">
          <h1 className="mb-4 text-grey-10 dark:text-white">{t("messages.title")}</h1>
          <LiveReconnectingNotice status={live} />
          <div className="lg:grid lg:grid-cols-[20rem_1fr] lg:gap-8">
            <div className={threadId ? "hidden lg:block" : ""}>
              {status === LoadingStatus.Failed && threads.length === 0 && (
                <p className="text-sm text-red-700 dark:text-red-400">{t("messages.loadFailed")}</p>
              )}
              {status !== LoadingStatus.Failed && threads.length === 0 && (
                <p className="text-sm text-grey-40 dark:text-grey-80">{t("messages.empty")}</p>
              )}
              <ThreadList threads={threads} activeThreadId={threadId} linkTo={(thread) => threadLink(thread.id)} />
            </div>
            <div className={threadId ? "" : "hidden lg:block"}>
              {active ? (
                <ThreadView
                  key={active.id}
                  channel={STAFF_CHANNEL}
                  thread={active}
                  emptyText={t("messages.threadEmpty")}
                  backTo={ROUTES.MESSAGES}
                  sessionLink={(bookingId) => ROUTES.SESSION_DETAILS.replace(":id", bookingId)}
                />
              ) : (
                threads.length > 0 && (
                  <p className="text-sm text-grey-40 dark:text-grey-80">{t("messages.noThreadSelected")}</p>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MessagesPage;
